import React, { useContext, useMemo } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { UserContext } from '../../../../../context/UserContext';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { Routing } from '../../../../../util/routes.util';
import { Breadcrumbs, Crumb } from '../../../../components/Breadcrumbs';
import { Tab, Tabs } from '../../../../components/Tabs';
import UserUpdateRolesPane from './Roles/UserUpdateRolesPane';
import UserUpdateAuthenticationPane from './Authentication/UserUpdateAuthenticationPane';
import UserUpdateCustomFieldPane from './CustomFields/UserUpdateCustomFieldPane';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../types/custom_field';
import UserUpdateManagementPane from './Management/UserUpdateManagementPane';

export default function UserUpdatePane() {
  const id = useParams()['id'] || '';

  const { companyRoles, isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { currentUser, companyUsers } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  if (!currentUser) return null;

  const companyRole = [...companyRoles.values()].flat().find(cra => cra.id === id);

  if (!companyRole) return null;
  const user = companyUsers.get(companyRole.userId);
  if (!user) return null;

  const userCustomFields = useMemo(() => {
    return [...customFields.values()].filter(cf => cf.entityType === CustomFieldEntityType.companyRole && !cf.hidden);
  }, [customFields, companyUsers]);

  return (
    <>
      <div className='flex flex-grow flex-col'>
        <Breadcrumbs>
          <Crumb path={Routing.settings.users.overview()} text={t(TranslationKey.userManagement).singular.upper} />
          {isAdmin ? <Crumb path={Routing.settings.users.update(id).roles()} text={user.fullName} current /> : null}
        </Breadcrumbs>

        <div className='content-x content-y-breadcrumbs flex h-full flex-grow flex-col'>
          <Tabs>
            <Tab text={t(TranslationKey.role).plural.upper} path={Routing.settings.users.update(id).roles()} />
            <Tab
              text={t(TranslationKey.authentication).singular.upper}
              path={Routing.settings.users.update(id).authentication()}
            />
            <Tab
              disabled={!userCustomFields.length}
              text={t(TranslationKey.customFields).singular.upper}
              path={Routing.settings.users.update(id).customFields()}
            />
            <Tab
              text={t(TranslationKey.userSettingsManagementTitle).singular.upper}
              path={Routing.settings.users.update(id).management()}
            />
          </Tabs>
          <Routes>
            <Route path={'/*'} element={<Navigate to={'/roles'} />} />
            <Route path={'/roles/*'} element={<UserUpdateRolesPane user={user} companyRole={companyRole} />} />
            <Route path={'/custom_fields/*'} element={<UserUpdateCustomFieldPane user={user} />} />
            {isAdmin && <Route path={'/authentication/*'} element={<UserUpdateAuthenticationPane user={user} />} />}
            {isAdmin && <Route path={'/management/*'} element={<UserUpdateManagementPane user={companyRole} />} />}
          </Routes>
        </div>
      </div>
    </>
  );
}
